import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useNavigate } from "@tanstack/react-router"
import { CloudMoon } from "lucide-react"
import { useForm } from "react-hook-form"
import { useErrorDialog } from "@/shared/components/ErrorDialog"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form"
import { Input } from "@/shared/components/ui/input"
import { ApiError } from "@/shared/utils/apiError"
import { useLoginMutation } from "./api/auth.mutations"
import { type LoginInput, type LoginValues, loginSchema } from "./auth.schemas"

export function LoginPage() {
  const navigate = useNavigate()
  const { showApiError } = useErrorDialog()
  const login = useLoginMutation()

  const form = useForm<LoginInput, unknown, LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "" },
  })

  const onSubmit = (values: LoginValues) => {
    login.mutate(values, {
      onSuccess: () => navigate({ to: "/" }),
      onError: (err) => {
        if (err instanceof ApiError && err.status === 401) {
          form.setError("password", { message: "Fel användarnamn eller lösenord" })
          return
        }
        showApiError(err, { title: "Kunde inte logga in" })
      },
    })
  }

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <CloudMoon className="size-6 text-accent" />
            Slumra
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Användarnamn</FormLabel>
                    <FormControl>
                      <Input autoComplete="username" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lösenord</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="mt-2 w-full" isLoading={login.isPending}>
                Logga in
              </Button>
            </form>
          </Form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Har du inget konto?{" "}
            <Link to="/register" className="text-accent underline-offset-4 hover:underline">
              Skapa konto
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  )
}
